import {
  Alert,
  Button,
  Fade,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'

import { LinkAndDocument } from '@/@types'
import { Svg } from '@/components'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { CirclePlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Modal } from '../../../components'
import { FileInput, FileUploader } from '../../../components/ui/file-upload'
import { useInputText } from '../../../hooks'
import { useData } from '../lessons/use-data'

import axios from 'axios'
import services from '../../../services'

import { ScrollArea } from '@/components/ui/scroll-area'
import { formatFileSize } from '@/resources/format-file-size'
import { useQueryClient } from '@tanstack/react-query'
import { dropzone } from './fileconfig'

interface ModalRegisterAttachmentProps {
  open: boolean
  onClose(): void
}

export default function ModalRegisterAndUpdate({
  open,
  onClose,
}: ModalRegisterAttachmentProps) {
  const initialValues = { name: '', description: '', link: '' }
  const input = useInputText(initialValues)
  const { lessons, isLoading } = useData()
  const queryClient = useQueryClient()
  const [selectedResourceType, setSelectedResourceType] = useState<
    'DOCUMENT' | 'LINK'
  >('DOCUMENT')

  const [lessonId, setLessonId] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [files, setFiles] = useState<File[] | null>([])

  const onChangeLessonId = (event: SelectChangeEvent) =>
    setLessonId(event.target.value)

  const closeModal = () => {
    input.clear()
    setError(null)
    queryClient.invalidateQueries({ queryKey: ['/documents'] })
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)
      const { name, link, description } = input
      const data: Partial<LinkAndDocument> = {
        type: selectedResourceType,
        description,
        label: name,
        lessonId,
      }

      if (selectedResourceType === 'DOCUMENT' && files) {
        data.file = {
          contentType: files[0].type,
          filename: files[0].name,
          size: files[0].size,
        }
      } else {
        data.url = link
      }

      const attachment = await services.attachments.create(data)

      if (selectedResourceType === 'DOCUMENT' && files) {
        console.log({ selectedResourceType, files, attachment })
        if (attachment?.uploadUrl) {
          try {
            await axios.put(attachment!.uploadUrl, files[0], {
              headers: { 'Content-Type': files[0].type },
            })
          } catch (error) {
            throw new Error('failed to upload files')
          }
        }
      }

      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open === false) {
      setFiles([])
    }
    return () => {
      setFiles([])
    }
  }, [open])

  useEffect(() => {
    console.log({ files })
    if (!input.name && files && files[0])
      input.updateInitial('name', files[0].name.split('.')[0])
    // eslint-disable-next-line
  }, [files]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2>Registar novo utilizador</h2>}
    >
      <ScrollArea className="h-[500px] w-full">
        <Fade in={open}>
          <div className="text-xl grid gap-8">
            {loading && <Alert severity="warning">A enviar dados...</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <RadioGroup
              defaultValue="DOCUMENT"
              value={selectedResourceType}
              onValueChange={(value: 'DOCUMENT' | 'LINK') => {
                setSelectedResourceType(value)
              }}
              className="flex items-start gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="DOCUMENT" id="DOCUMENT" />
                <Label htmlFor="DOCUMENT" className="text-base">
                  Documento
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="LINK" id="LINK" />
                <Label htmlFor="LINK" className="text-base">
                  Ligação
                </Label>
              </div>
            </RadioGroup>

            <form onSubmit={handleSubmit} className="grid gap-4">
              {selectedResourceType === 'DOCUMENT' && (
                <div className="grid gap-4">
                  <FileUploader
                    value={files}
                    onValueChange={setFiles}
                    dropzoneOptions={dropzone}
                  >
                    <FileInput>
                      <div className="flex items-center gap-2 justify-center h-14 w-full border bg-transparent rounded-xl">
                        <CirclePlus className="h-4 w-4" />

                        {files?.length === 0 ? (
                          <p className="text-gray-400 text-sm font-semibold">
                            Arraste o ficheiro ou clique aqui
                          </p>
                        ) : (
                          <>
                            <p className="text-gray-400 text-sm font-semibold">
                              {files?.length ? files[0].name : ''} -{' '}
                              {files && formatFileSize(files[0].size)}
                            </p>
                          </>
                        )}
                      </div>
                    </FileInput>
                  </FileUploader>
                </div>
              )}

              {selectedResourceType === 'LINK' && (
                <div className="grid gap-4">
                  <TextField
                    autoFocus
                    required
                    label="Ligação"
                    name="link"
                    value={input.link}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                    error={!!input.errors.link}
                    helperText={input.errors.link}
                    disabled={loading}
                  />
                </div>
              )}
              <div className="grid gap-4">
                <TextField
                  autoFocus
                  required
                  label="Nome"
                  name="name"
                  value={input.name}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.name}
                  helperText={input.errors.name}
                  disabled={loading}
                />
              </div>

              <div className="grid gap-4">
                <Select
                  value={lessonId}
                  onChange={onChangeLessonId}
                  disabled={isLoading}
                  label="Aula"
                  displayEmpty
                  required
                >
                  <MenuItem value="" disabled>
                    Escolha a aula
                  </MenuItem>
                  {lessons?.map((lesson) => (
                    <MenuItem key={lesson.id} value={lesson.id}>
                      {lesson.title}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className="grid gap-4">
                <Textarea
                  value={input.description}
                  name="description"
                  onChange={input.onChange}
                  placeholder="Descrição"
                  rows={4}
                  className="text-[17px] placeholder:text-slate-400"
                />
              </div>

              <div className="w-max max-w-full mx-auto mt-4 gap-4 flex">
                <Button disabled={loading || lessonId === ''} type="submit">
                  <Svg src="/icons/save.svg" className="max-w-4" />
                  <span className="font-bold capitalize text-base">Criar</span>
                </Button>
              </div>
            </form>
          </div>
        </Fade>
      </ScrollArea>
    </Modal>
  )
}
